// Stage 73.20dp — ESS Assets & Documents (read-only viewer).
//
// Route: /ess/assets-docs
// RBAC: allow all employees (any authenticated user with an employee record).
//
// Shows the assets mapped to the current employee + attached documents,
// mirroring what admins see on /employees/{id}/assets-docs. Employees
// cannot edit / upload / delete — this is a strict viewer.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Package, FileText, Download, Loader2, X, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CHIP = {
  issued: 'bg-blue-50 text-blue-700 border border-blue-100',
  returned: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  lost: 'bg-rose-50 text-rose-700 border border-rose-100',
  damaged: 'bg-amber-50 text-amber-700 border border-amber-100',
  written_off: 'bg-slate-100 text-slate-600 border border-slate-200',
};

const inr = (n) => Number(n || 0).toLocaleString('en-IN');

export default function ESSAssetsDocs() {
  const [assets, setAssets] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [assetDocs, setAssetDocs] = useState([]);
  const [assetDocsLoading, setAssetDocsLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, d] = await Promise.all([
        axios.get(`${API}/ess/my-assets`, { withCredentials: true }),
        axios.get(`${API}/ess/my-documents?include_asset_related=false`, { withCredentials: true }),
      ]);
      setAssets(a.data || []);
      setDocs(d.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load your assets & documents.');
    } finally {
      setLoading(false);
    }
  };

  const openAssetDocs = async (asset) => {
    setDetailAsset(asset);
    setAssetDocs([]);
    setAssetDocsLoading(true);
    try {
      const r = await axios.get(
        `${API}/ess/my-assets/${asset.id}/documents`,
        { withCredentials: true },
      );
      setAssetDocs(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load documents.');
    } finally {
      setAssetDocsLoading(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(
        `${API}/ess/my-documents/${doc.id}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed.');
    }
  };

  return (
    <MainLayout>
      <div data-testid="ess-assets-docs-page" className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-[#0052FF]" /> My Assets &amp; Documents
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            View the assets issued to you along with the supporting documents.
            Contact HR / Admin for any changes.
          </p>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 flex items-start gap-2 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {/* Assets */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <Package className="w-4 h-4" /> Issued Assets
              <span className="text-xs text-slate-500 font-normal">
                ({assets.length} total)
              </span>
            </h2>
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…
            </div>
          ) : assets.length === 0 ? (
            <p className="text-sm text-slate-500 italic p-6">
              No assets issued to you yet.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="ess-assets-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Type</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Asset / Model</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Serial</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Handover</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Return Due</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Status</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Docs</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`ess-asset-row-${a.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-2 text-slate-700">{a.asset_type}</td>
                    <td className="px-4 py-2 font-medium text-slate-800">{a.asset_name}</td>
                    <td className="px-4 py-2 text-slate-600">{a.serial_number || '—'}</td>
                    <td className="px-4 py-2 text-slate-600">{a.handover_date}</td>
                    <td className="px-4 py-2 text-slate-600">{a.expected_return_date || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CHIP[a.status] || STATUS_CHIP.issued}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`ess-asset-docs-btn-${a.id}`}
                        onClick={() => openAssetDocs(a)}
                        className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:underline"
                        title="View documents linked to this asset"
                      >
                        <FileText className="w-3.5 h-3.5" /> Docs
                        {(a.document_count || 0) > 0 && (
                          <span
                            data-testid={`ess-asset-doc-badge-${a.id}`}
                            className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold inline-flex items-center justify-center"
                          >
                            {a.document_count}
                          </span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Generic docs (non asset-linked) — informational */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="w-4 h-4" /> My Documents
              <span className="text-xs text-slate-500 font-normal">
                ({docs.length} generic docs)
              </span>
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Personal &amp; HR documents mapped to your record. Asset-linked
              docs live under each asset above.
            </p>
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…
            </div>
          ) : docs.length === 0 ? (
            <p className="text-sm text-slate-500 italic p-6">
              No documents mapped to you yet.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="ess-docs-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">File</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Category</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Uploaded</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`ess-doc-row-${d.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-2 font-medium text-slate-800">{d.filename}</td>
                    <td className="px-4 py-2 text-slate-600">{d.category}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{(d.uploaded_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`ess-doc-download-${d.id}`}
                        onClick={() => downloadDoc(d)}
                        className="inline-flex items-center gap-1 text-xs text-[#0052FF] hover:underline"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Per-asset docs modal */}
        {detailAsset && (
          <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setDetailAsset(null)}
            data-testid="ess-asset-docs-modal"
          >
            <div
              className="bg-white rounded-lg p-5 max-w-2xl w-full max-h-[88vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Documents linked to{' '}
                    <span className="text-blue-700">{detailAsset.asset_name}</span>
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {detailAsset.asset_type}
                    {detailAsset.serial_number ? ` · Serial ${detailAsset.serial_number}` : ''}
                    {' · '}{assetDocs.length} attached
                  </p>
                </div>
                <button onClick={() => setDetailAsset(null)} aria-label="Close">
                  <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                </button>
              </div>

              <div className="border border-slate-200 rounded overflow-hidden">
                {assetDocsLoading ? (
                  <div className="p-4 text-center text-xs text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline-block" /> Loading…
                  </div>
                ) : assetDocs.length === 0 ? (
                  <p className="text-xs text-slate-500 italic p-3">
                    No documents attached to this asset yet.
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">File</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Uploaded</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetDocs.map((d) => (
                        <tr key={d.id} className="border-t border-slate-100">
                          <td className="p-2 font-medium">{d.filename}</td>
                          <td className="p-2 text-slate-600">{d.category}</td>
                          <td className="p-2 text-slate-500 text-[11px]">
                            {(d.uploaded_at || '').slice(0, 10)}
                          </td>
                          <td className="p-2 text-right">
                            <button
                              data-testid={`ess-asset-doc-download-${d.id}`}
                              onClick={() => downloadDoc(d)}
                              className="text-[#0052FF] hover:underline text-xs inline-flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex justify-end mt-3">
                <button
                  onClick={() => setDetailAsset(null)}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
